import { type GqlExecutor } from '@hopdrive/sdk-core'
import type { BuildEventInput, EventlogInsertInput } from '../../types'
import type { EventKey } from '../../types/event-keys'
import { EventValidationError } from '../../errors/event-errors'
import { getEventKeyDefinition } from '../../registry'
import { getRoleByName } from '../lookup-types/getRoleByName.js'

const EVENT_KEY_PATTERN = /^[a-z][a-z_]*(\.[a-z][a-z_]*)*$/

function coerceId(value: unknown): number | undefined {
  if (value === undefined || value === null || value === 0 || value === '') return undefined
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const num = Number(value)
    if (num === 0) return undefined
    if (Number.isFinite(num)) return num
  }
  return value as never
}

export async function buildEvent(
  options: BuildEventInput,
  executor?: GqlExecutor
): Promise<EventlogInsertInput> {
  const { key, user, role } = options

  if (typeof key !== 'string' || !EVENT_KEY_PATTERN.test(key)) {
    throw new EventValidationError('key', 'Key must contain only lowercase letters, underscores, and dots (e.g. "move.created", "moves.cancel_status.changed")')
  }
  if (!user || typeof user !== 'string') {
    throw new EventValidationError('user', 'User is required and must be a string')
  }
  if (!role || typeof role !== 'string') {
    throw new EventValidationError('role', 'Role is required and must be a string')
  }

  const safeMetadata = options.metadata || undefined
  if (safeMetadata !== undefined && typeof safeMetadata !== 'object') {
    throw new EventValidationError('metadata', 'Metadata must be an object')
  }

  const safeCustomerId = coerceId(options.customer_id)
  if (safeCustomerId !== undefined && typeof safeCustomerId !== 'number') {
    throw new EventValidationError('customer_id', 'customer_id must be a number')
  }

  const safeMoveId = coerceId(options.move_id)
  if (safeMoveId !== undefined && typeof safeMoveId !== 'number') {
    throw new EventValidationError('move_id', 'move_id must be a number')
  }

  const safeLaneId = coerceId(options.lane_id)
  if (safeLaneId !== undefined && typeof safeLaneId !== 'number') {
    throw new EventValidationError('lane_id', 'lane_id must be a number')
  }

  const safeLocationId = coerceId(options.location_id)
  if (safeLocationId !== undefined && typeof safeLocationId !== 'number') {
    throw new EventValidationError('location_id', 'location_id must be a number')
  }

  const safeDriverId = coerceId(options.driver_id)
  if (safeDriverId !== undefined && typeof safeDriverId !== 'number') {
    throw new EventValidationError('driver_id', 'driver_id must be a number')
  }

  const safeRegionId = coerceId(options.region_id)
  if (safeRegionId !== undefined && typeof safeRegionId !== 'number') {
    throw new EventValidationError('region_id', 'region_id must be a number')
  }

  const safePlateId = coerceId(options.plate_id)
  if (safePlateId !== undefined && typeof safePlateId !== 'number') {
    throw new EventValidationError('plate_id', 'plate_id must be a number')
  }

  const safeOrganizationId = coerceId(options.organization_id)
  if (safeOrganizationId !== undefined && typeof safeOrganizationId !== 'number') {
    throw new EventValidationError('organization_id', 'organization_id must be a number')
  }

  if (!options.skipMetadataValidation) {
    const def = getEventKeyDefinition(key as EventKey)
    if (def?.metadataSchema && safeMetadata !== undefined) {
      const result = def.metadataSchema.safeParse(safeMetadata)
      if (!result.success) {
        throw new EventValidationError('metadata', `Metadata does not match schema for "${key}": ${result.error.message}`)
      }
    }
  }

  const roleType = await getRoleByName(role, executor)

  const eventlog: EventlogInsertInput = {
    event_time_utc: new Date().toISOString(),
    event_key: key,
    user,
    role_id: roleType.id,
    metadata: safeMetadata,
    customer_id: safeCustomerId,
    move_id: safeMoveId,
    lane_id: safeLaneId,
    location_id: safeLocationId,
    driver_id: safeDriverId,
    region_id: safeRegionId,
    plate_id: safePlateId,
    organization_id: safeOrganizationId,
  }

  return eventlog
}
